/**
 * Hook for creating risks from threat templates
 *
 * Provides a reusable function to create risks from:
 * - ThreatTemplate (from threat-profile API)
 * - SubtechniqueInfo (from subtechniques API)
 * - AssistantSuggestion (from Security Assistant)
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { riskApi } from '../services/risk.service';
import type {
  ThreatTemplate,
  SubtechniqueInfo,
} from '../services/components.service';
import type { CreateRiskDto, Risk, RiskTreatment } from 'shared';

export interface CreateRiskFromThreatParams {
  componentId: string;
  spaceId: string;
  threat?: ThreatTemplate;
  subtechnique?: SubtechniqueInfo;
  parentThreat?: ThreatTemplate; // Parent threat when creating from subtechnique
}

export interface UseCreateRiskFromThreatResult {
  createRiskFromThreat: (
    params: CreateRiskFromThreatParams
  ) => Promise<Risk | null>;
  isCreating: boolean;
  error: string | null;
  lastCreatedRisk: Risk | null;
}

export function useCreateRiskFromThreat(): UseCreateRiskFromThreatResult {
  const { t } = useTranslation();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastCreatedRisk, setLastCreatedRisk] = useState<Risk | null>(null);

  const createRiskFromThreat = useCallback(
    async (params: CreateRiskFromThreatParams): Promise<Risk | null> => {
      const { componentId, spaceId, threat, subtechnique, parentThreat } =
        params;

      if (!threat && !subtechnique) {
        setError(t('risk.errors.noThreatData'));
        return null;
      }

      setIsCreating(true);
      setError(null);

      try {
        let riskDto: CreateRiskDto;

        if (subtechnique) {
          // Creating risk from a subtechnique
          const parent = parentThreat;

          riskDto = {
            title: `${subtechnique.name}`,
            description:
              subtechnique.description ||
              t('risk.defaults.subtechniqueDescription', {
                name: subtechnique.name,
                id: subtechnique.id,
              }),
            threatSource: t('risk.defaults.threatSource'),
            vulnerability: t('risk.defaults.vulnerability', {
              technique: subtechnique.name,
            }),
            existingControls: [],
            attackTechniqueId: subtechnique.id,
            // Use parent threat's CIA if available, otherwise defaults
            confidentialityImpact:
              parent?.confidentialityImpact ||
              parent?.ciaImpact?.confidentiality ||
              5,
            integrityImpact:
              parent?.integrityImpact || parent?.ciaImpact?.integrity || 5,
            availabilityImpact:
              parent?.availabilityImpact ||
              parent?.ciaImpact?.availability ||
              5,
            likelihood: parent?.likelihood || 5,
            riskTreatment: 'mitigate' as RiskTreatment,
            owner: t('risk.defaults.owner'),
            componentId,
            spaceId,
          };
        } else if (threat) {
          // Creating risk from a main threat
          riskDto = {
            title: threat.title || threat.attackTechniqueName,
            description:
              threat.description ||
              t('risk.defaults.threatDescription', {
                name: threat.attackTechniqueName,
                id: threat.attackTechniqueId,
              }),
            threatSource:
              threat.threatSource || t('risk.defaults.threatSource'),
            vulnerability:
              threat.vulnerability ||
              t('risk.defaults.vulnerability', {
                technique: threat.attackTechniqueName,
              }),
            existingControls: [],
            attackTechniqueId: threat.attackTechniqueId,
            confidentialityImpact:
              threat.confidentialityImpact ||
              threat.ciaImpact?.confidentiality ||
              5,
            integrityImpact:
              threat.integrityImpact || threat.ciaImpact?.integrity || 5,
            availabilityImpact:
              threat.availabilityImpact || threat.ciaImpact?.availability || 5,
            likelihood: threat.likelihood || 5,
            riskTreatment: 'mitigate' as RiskTreatment,
            owner: t('risk.defaults.owner'),
            componentId,
            spaceId,
          };
        } else {
          throw new Error('No threat or subtechnique provided');
        }

        const createdRisk = await riskApi.create(riskDto);
        setLastCreatedRisk(createdRisk);

        console.info(
          '[useCreateRiskFromThreat] Risk created:',
          createdRisk.title,
          '- ID:',
          createdRisk.id
        );

        return createdRisk;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : t('risk.errors.createFailed');
        setError(errorMessage);
        console.error('[useCreateRiskFromThreat] Failed to create risk:', err);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    [t]
  );

  return {
    createRiskFromThreat,
    isCreating,
    error,
    lastCreatedRisk,
  };
}
